import { useEffect, useState } from "react";
import { Box, Stack, Typography, Paper, Link, Button } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { BRAND } from "../../styles/theme";
import type { CollectorCtx } from "../../pages/CollectorDetail";
import { listUploadTasks, UploadTask, downloadContractPdf } from "../../services/collectors";
import { getCollectorSummary } from "../../services/collectorStats";
import { fmtDateTime } from "./chips";

function Tile({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Box sx={{ bgcolor: BRAND.primaryLight, borderRadius: 2, px: 2.25, py: 1.75, minWidth: 180, flex: 1 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary" }}>{label}</Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.15, mt: 0.5, color: color ?? BRAND.primary, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}

/** Overview (nuevo en v2): ¿está bien configurado el collector? sin leer 30 keys. */
export default function OverviewTab({ ctx, onGo }: { ctx: CollectorCtx; onGo: (t: "config" | "accounts" | "users" | "onboarding") => void }) {
  const { collector, config, catalog } = ctx;
  const [users, setUsers]   = useState<number | null>(null);
  const [lastUp, setLastUp] = useState<UploadTask | null | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    // CP-354: antes esto llamaba a listUsers() (GET /Admin/collector/login_user),
    // que pagina sin límite en el backend y puede no responder para collectors con
    // muchos usuarios (confirmado: 540 en LaNuevaTextilConsignacion), tumbando todo
    // el Overview con "Failed to fetch". El conteo agregado de collector_stats ya
    // existe para la lista de Collectors (CP-442) y es barato — se usa acá en vez
    // de pedir la lista completa solo para un número. El detalle de usuarios (y el
    // chequeo de si hay alguno con rol "main") queda para la tab Users, que ya lo
    // carga bajo demanda.
    getCollectorSummary().then((s) => alive && setUsers(s.items.find((i) => i.id === collector.id)?.users ?? null)).catch(() => alive && setUsers(null));
    listUploadTasks(collector.id).then((t) => alive && setLastUp([...t].sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))[0] ?? null)).catch(() => alive && setLastUp(null));
    return () => { alive = false; };
  }, [collector.id]);

  const rate = Number(config.charge_rate);
  const cfgId = encodeURIComponent(collector.id);
  const warnings: { sev: "warn" | "crit"; text: JSX.Element; to: string }[] = [];
  if (!config.close_id)         warnings.push({ sev: "warn", text: <>No <code>close_id</code> — the collector will not close automatically.</>, to: `/collector/${cfgId}/config?q=close_id` });
  if (!config.charge_rate)      warnings.push({ sev: "crit", text: <>No <code>charge_rate</code> — fees can't be calculated.</>, to: `/collector/${cfgId}/config?q=charge_rate` });
  if (!config.contract_version) warnings.push({ sev: "warn", text: <>No contract template set (<code>contract_version</code>).</>, to: `/collector/${cfgId}/config?q=contract` });
  if (!config.company_name)     warnings.push({ sev: "warn", text: <>No <code>company_name</code> (legal name).</>, to: `/collector/${cfgId}/config?q=company_name` });
  if (users === 0) warnings.push({ sev: "crit", text: <>No vendor web users yet.</>, to: `/collector/${cfgId}/users` });

  const addr = [
    [config.data_address_street, config.data_address_street_number].filter(Boolean).join(" "),
    [config.data_address_city, config.data_address_state, config.data_address_postal_code].filter(Boolean).join(", "),
  ].filter(Boolean).join(" · ");

  return (
    <>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
        <Tile label="Users" value={users != null ? String(users) : "…"} sub="vendor web" />
        <Tile label="Config keys" value={`${Object.keys(config).length}`} sub={`of ${Object.keys(catalog).length} in catalog`} />
        <Tile label="Fee" value={config.charge_rate && !isNaN(rate) ? `${(rate * 100).toFixed(3).replace(/0+$/, "").replace(/\.$/, "")}%` : "—"}
          sub={config.charge_rate ? `charge_rate ${config.charge_rate}` : "charge_rate not set"} color={config.charge_rate ? undefined : "#9A6700"} />
        <Tile label="Accounts" value={(config.create_accounts ?? "").toString().toLowerCase() === "false" ? "Closed" : "Open"}
          sub={(config.create_accounts ?? "").toString().toLowerCase() === "false" ? "creation disabled" : "creation enabled"} />
        <Tile label="Last upload" value={lastUp === undefined ? "…" : lastUp ? lastUp.status : "—"} sub={lastUp ? fmtDateTime(lastUp.createdAt) : "no uploads yet"}
          color={lastUp ? (/ERR|FAIL/i.test(lastUp.status) ? "#9A6700" : "#166534") : undefined} />
        <Tile label="API key" value={config.apiKeyHash ? "Issued" : "—"} sub={config.apiKeyLastUse ? `last used ${fmtDateTime(config.apiKeyLastUse)}` : "not used yet"} />
      </Stack>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>Needs attention</Typography>
          {warnings.length === 0
            ? <Typography variant="body2" color="text.secondary">Everything essential is configured.</Typography>
            : <Stack spacing={1}>
                {warnings.map((w, i) => (
                  <Stack key={i} direction="row" spacing={1.25} alignItems="flex-start">
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", mt: 0.9, flex: "none", bgcolor: w.sev === "crit" ? "#DC2626" : "#F59E0B" }} />
                    <Typography variant="body2">{w.text} <Link component={RouterLink} to={w.to} sx={{ fontWeight: 500 }}>Fix</Link></Typography>
                  </Stack>
                ))}
              </Stack>}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>Company &amp; contract</Typography>
          <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "170px 1fr", gap: "6px 16px", m: 0, fontSize: 13.5, "& dt": { color: "text.secondary" }, "& dd": { m: 0, wordBreak: "break-word" } }}>
            <dt>Legal name</dt><dd>{config.company_name || "—"}</dd>
            <dt>Address</dt><dd>{addr || "—"}</dd>
            <dt>Representative</dt><dd>{config.data_power_name ? <>{config.data_power_name} {config.data_power_lastname}{config.data_power_id && <Typography component="span" variant="caption" color="text.secondary"> · ID {config.data_power_id}</Typography>}</> : "—"}</dd>
            <dt>Contract</dt><dd>{config.contract_version ? <><code>{config.contract_version}</code> <Link component="button" onClick={() => downloadContractPdf(collector.id).catch(() => undefined)}>PDF</Link></> : "—"}</dd>
            <dt>Billing company</dt><dd>{config.billing_company || "default"}</dd>
            <dt>Acquirer</dt><dd style={{ fontFamily: "monospace" }}>{config.acquirer || "—"}</dd>
          </Box>
          <Button size="small" sx={{ mt: 1.5 }} onClick={() => onGo("config")}>Edit in Config →</Button>
        </Paper>
      </Stack>
    </>
  );
}
